<template>
  <v-card color="#f0f8f6" class="overflow-hidden rounded-xl">
    <div class="text-right ma-3">
      <v-btn icon @click="$emit('cancel')">
        <v-icon large>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-row justify="center" align="center" class="pb-5">
      <v-col cols="16" class="text-center text-h4">
        <div>Назначить просмотр продукта</div>
      </v-col>
    </v-row>
    <v-card-text>
      <div class="search-box mb-3">
        <v-text-field
          outlined
          dense
          rounded
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Поиск по ID, названию или номеру сессии"
          single-line
          hide-details
          clearable
          class="search-field"
        />
      </div>
      <div v-if="searchQuery && searchQuery.trim()">
        <div v-if="searchResults.length > 0" class="search-results">
          <div
            v-for="result in searchResults"
            :key="result.product.id"
            class="search-result-item"
            :class="{
              'search-result-item--selected': selected_products.includes(
                result.product
              ),
            }"
            @click="toggleSearchProduct(result.product)"
          >
            <v-icon
              class="search-result-item__check"
              :color="
                selected_products.includes(result.product)
                  ? 'teal darken-1'
                  : 'grey lighten-1'
              "
            >
              {{
                selected_products.includes(result.product)
                  ? "mdi-checkbox-marked"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
            <div class="search-result-item__content">
              <div class="search-result-item__name">
                {{ result.categoryName }}
              </div>
              <div class="search-result-item__meta">
                {{ result.product.product_type }} · Сессия №{{
                  result.product.session_number
                }}
                · ID: {{ result.product.id }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="search-empty">
          <v-icon color="grey lighten-1" size="32">mdi-magnify-close</v-icon>
          <span>Ничего не найдено</span>
        </div>
      </div>
      <v-card v-else elevation="0" class="rounded-xl tree-card" id="tree">
        <v-treeview :items="categories" @update:open="renderTreeNode">
          <template slot="label" slot-scope="{ item }">
            <div v-if="item.kinescope_id">
              <v-list>
                <v-list-group
                  v-for="p in [item].filter((i) => i.kinescope_id)"
                  :key="p.id"
                >
                  <template v-slot:activator>
                    <v-list-item-content>
                      <v-list-item-title
                        class="text-body-2"
                        style="max-width: 500px"
                        v-text="`Сессия №${p.session_number}`"
                      />
                    </v-list-item-content>
                  </template>
                  <v-list-item :key="p.id + item.kinescope_id">
                    <v-simple-checkbox
                      :value="selected_products.includes(p)"
                      @input="
                        selected_products.includes(p)
                          ? doUnselectSession(p)
                          : doSelectSession(p)
                      "
                      color="primary"
                    />
                    <v-list-item-title class="text-body-2">
                      {{ p.session_number }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list-group>
              </v-list>
            </div>
            <div
              v-else
              :style="
                item.children.length === 0
                  ? { marginLeft: '30px' }
                  : { display: 'flex', gap: '10px' }
              "
            >
              {{ item.name }}
              <v-simple-checkbox
                v-if="
                  ['PC', 'VR', 'MG'].includes(item.name) &&
                  item.children.length !== 0
                "
                :value="
                  !item.children.some(
                    (product) => !selected_products.includes(product)
                  )
                "
                @input="
                  !item.children.some(
                    (product) => !selected_products.includes(product)
                  )
                    ? unselectAll(item.children)
                    : selectAll(item.children)
                "
              />
            </div>
          </template>
        </v-treeview>
      </v-card>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-btn
        class="white--text"
        style="
          background-color: #d86568;
          width: 150px;
          height: 50px;
          border-radius: 50px;
        "
        @click="$emit('confirm', [selected_products])"
        :disabled="selected_products.length === 0"
      >
        Назначить
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { CATEGORIES } from "@/utils/constants";

const ALL_CATEGORIES_FLAT = (() => {
  const result = [];
  const flatten = (nodes) => {
    for (const node of nodes) {
      result.push(node);
      if (node.children && node.children.length) flatten(node.children);
    }
  };
  flatten(CATEGORIES);
  return result;
})();

export default {
  name: "SelectProductsForm",
  components: {},
  data() {
    return {
      selected_products: [],
      renderedNodes: [],
      searchQuery: "",
      categories: JSON.parse(JSON.stringify(CATEGORIES)),
    };
  },
  computed: {
    ...mapGetters("products", ["products"]),
    ...mapGetters("modals", ["modalFormComponent"]),
    searchResults() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return [];
      return (this.products || [])
        .filter((p) => {
          const cat = ALL_CATEGORIES_FLAT.find((c) => c.id === p.category_id);
          return (
            p.id.toString().includes(q) ||
            p.session_number?.toString().includes(q) ||
            (cat &&
              (cat.id.toString().includes(q) ||
                cat.name.toLowerCase().includes(q)))
          );
        })
        .map((p) => ({
          product: p,
          categoryName:
            ALL_CATEGORIES_FLAT.find((c) => c.id === p.category_id)?.name ||
            `Категория ${p.category_id}`,
        }))
        .sort((a, b) => a.product.session_number - b.product.session_number);
    },
    title() {
      return this.modalFormComponent?.title;
    },
    desc() {
      return this.modalFormComponent?.desc;
    },
  },
  methods: {
    addProductsToCategory(category) {
      // Фильтрация по id категории и типу продукта
      // Сортировка по номеру сессии в порядке возрастания
      const PCProducts = this.products
        .filter(
          (product) =>
            product.category_id === category.id && product.product_type === "PC"
        )
        .sort((a, b) => a.session_number - b.session_number);
      const VRProducts = this.products
        .filter(
          (product) =>
            product.category_id === category.id && product.product_type === "VR"
        )
        .sort((a, b) => a.session_number - b.session_number);
      const MGProducts = this.products
        .filter(
          (product) =>
            product.category_id === category.id && product.product_type === "MG"
        )
        .sort((a, b) => a.session_number - b.session_number);

      // Добавление продуктов в подкатегории
      category.children = [
        {
          parentId: category.id,
          id: 100 + category.id,
          type: "PC",
          name: "PC",
          children: PCProducts,
        },
        {
          parentId: category.id,
          id: 200 + category.id,
          type: "VR",
          name: "VR",
          children: VRProducts,
        },
        {
          parentId: category.id,
          id: 300 + category.id,
          type: "MG",
          name: "MG",
          children: MGProducts,
        },
      ];
    },
    renderTreeNode(openedNodes) {
      // Рендер только впервые открытых категорий
      if (
        openedNodes.length > 0 &&
        !this.renderedNodes.includes(openedNodes[openedNodes.length - 1])
      ) {
        this.renderedNodes.push(openedNodes[openedNodes.length - 1]);
      } else {
        return;
      }

      // Для категорий PC, VR, MG рендер не нужен
      if (openedNodes[openedNodes.length - 1] >= 100) return;

      // Поиск открытой категории по её id
      function findItemById(categories, id) {
        for (const category of categories) {
          if (category.id === id) {
            return category;
          }
          if (category.children) {
            const found = findItemById(category.children, id);
            if (found) {
              return found;
            }
          }
        }
      }
      const openedItem = findItemById(
        this.categories,
        openedNodes[openedNodes.length - 1]
      );

      // Наполнение категорий PC, VR, MG продуктами
      if (openedItem.isEndNode) {
        this.addProductsToCategory(openedItem);
        return;
      }

      // Вставка {} в children конечных категорий
      // для того чтобы появилась возможность их открыть
      openedItem.children.forEach((child) => {
        if (child.children.length === 0) {
          child.children = [{}];
          child.isEndNode = true;
        }
      });
    },
    toggleSearchProduct(p) {
      if (this.selected_products.includes(p)) {
        this.doUnselectSession(p);
      } else {
        this.doSelectSession(p);
      }
    },
    doSelectSession(p) {
      this.selected_products.push(p);
    },
    doUnselectSession(p) {
      this.selected_products = this.selected_products.filter(
        (item) => p !== item
      );
    },
    selectAll(toSelect) {
      toSelect = toSelect.filter(
        (item) => !this.selected_products.includes(item)
      );
      toSelect.forEach((p) => this.doSelectSession(p));
    },
    unselectAll(toUnselect) {
      toUnselect.forEach((p) => this.doUnselectSession(p));
    },
  },
};
</script>

<style scoped lang="scss">
.v-treeview {
  width: max-content;
}

.search-box {
  padding: 0 4px;
}

.search-field {
  ::v-deep .v-input__slot {
    background: #f0f8f6 !important;
  }
}

.search-results {
  border: 1.5px solid #e0eeeb;
  border-radius: 12px;
  overflow: hidden;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f0f8f6;
  }
  &--selected {
    background: #e6f4f1;
  }

  &__check {
    flex-shrink: 0;
  }
  &__content {
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a1a;
    word-break: break-word;
    white-space: normal;
    line-height: 1.4;
  }

  &__meta {
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 2px;
  }
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #bdbdbd;
  font-size: 14px;
}

.tree-card {
  border: 1.5px solid #e0eeeb;
  background: #fafffe;
}

@import "~vuetify/src/styles/styles.sass";

@media #{map-get($display-breakpoints, 'xs-only')} {
  .v-card #tree {
    overflow-x: scroll;
  }
}
</style>

<style lang="scss">
.v-list-group__header__append-icon {
  order: -1;
  margin-left: -25px !important;
  margin-right: 5px !important;
}
</style>
